import React, { type Ref, type RefObject, useEffect, useState } from "react";
import { useCloseOnOutsideClick, useShowAfterFirstRender } from "../hooks";
import { type UseShouldShowModalInsteadType, useShouldShowModal } from "./useShouldShowModal";
import { showModal } from "../redux/actions/ModalActions";
import { MODAL_CONTENT } from "../utils/modalConstants";
import { useDispatch } from "../../shared-logic/redux/store";

export enum OffsetAlignment {
    Left = "left",
    Right = "right",
    Top = "top",
    Bottom = "bottom",
}

export type OffsetAlignType = {
    horizontal: OffsetAlignment.Left | OffsetAlignment.Right;
    vertical: OffsetAlignment.Top | OffsetAlignment.Bottom;
} | null;

export type OffsetType = {
    align: OffsetAlignType;
    x: number;
    y: number;
};

export type PopoverTooltipType = {
    children?: React.ReactNode;
    content?: string;
    forceShowModal?: boolean;
    hide?: boolean;
    iconRef: RefObject<HTMLElement>;
    id?: string;
    initializeTooltipAlignment?: boolean;
    onClose: () => void;
    onKeyDown?: (event: React.KeyboardEvent) => void;
    openedOnHover?: boolean;
    role?: string;
    small?: boolean;
    tabIndex?: number;
    useHTML?: boolean;
    zIndex?: number;
};

export const useAlignElementToOtherElement = (
    iconRef: RefObject<HTMLElement>,
    tooltipRef: RefObject<HTMLElement>,
    tooltipVisible?: boolean,
): OffsetType => {
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [align, setAlign] = useState<OffsetAlignType>(null);
    const iconTopOffset = iconRef.current?.getBoundingClientRect().top;

    // Calculate tooltip position.
    useEffect(() => {
        if (!tooltipVisible) return;

        const updatePosition = (): void => {
            if (!iconRef.current || !tooltipRef.current || !tooltipVisible) return;

            const triggerRect = iconRef.current.getBoundingClientRect();
            const tooltipRect = tooltipRef.current.getBoundingClientRect();

            // Calculate the available space above and below the trigger element
            const spaceAbove = triggerRect.top;

            // Determine whether to position the tooltip above or below the trigger HTMLDivElement
            const pos = spaceAbove >= tooltipRect.height ? "above" : "below";
            const yPos = pos === "above" ? triggerRect.top - (tooltipRect.height + 12) : triggerRect.bottom + 12;
            let xPos = triggerRect.left - 30;

            // When there's a tooltip icon at the side of the screen,
            // then the tooltip is going (partially) outside of the viewport
            // so I will reset the position of that tooltip
            if (triggerRect.left + tooltipRect.width >= window.innerWidth) {
                xPos = triggerRect.left - tooltipRect.width + 48;
            }

            setOffset({ x: xPos, y: yPos });
            setAlign({
                horizontal:
                    triggerRect.left + tooltipRect.width >= window.innerWidth
                        ? OffsetAlignment.Right
                        : OffsetAlignment.Left,
                vertical: triggerRect.top - tooltipRect.height <= 20 ? OffsetAlignment.Top : OffsetAlignment.Bottom,
            });
        };

        updatePosition();
        window.addEventListener("resize", updatePosition);
        window.addEventListener("scroll", updatePosition);

        return () => {
            window.removeEventListener("resize", updatePosition);
            window.removeEventListener("scroll", updatePosition);
        };
    }, [iconRef, tooltipRef, iconTopOffset, tooltipVisible]);

    return { align, ...offset };
};

type UsePopOverTooltipType = UseShouldShowModalInsteadType & {
    tooltipRef: RefObject<HTMLDivElement>;
    pointOffset: OffsetAlignType;
    show: boolean;
    offsetWithArrowLocation: { x: number; y: number };
};

export const usePopOverTooltip = (
    props: PopoverTooltipType,
    forwardedRef?: Ref<HTMLElement>,
    forceShowModal?: boolean,
): UsePopOverTooltipType => {
    const { iconRef, children, hide, onClose, useHTML = false, content = "", openedOnHover = false } = props;
    const dispatch = useDispatch();
    const tooltipRef = (forwardedRef || React.createRef()) as RefObject<HTMLDivElement>;

    // Show after first render as we need DOM dimensions for this component to work.
    const show = useShowAfterFirstRender();

    // Register close on outside click, this should not have impact if the
    // tooltip is a modal. disable outsideClick logic if tooltip is not shown and hoverTriggered
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    useCloseOnOutsideClick(tooltipRef, !openedOnHover ? onClose : () => {}, hide);

    const { align, x: xPos, y: yPos } = useAlignElementToOtherElement(iconRef, tooltipRef, !hide);

    // Check if a modal should be used instead of rendering a tooltip.
    // This will happen on first render because we need to know the size of the tooltip.
    const { tooltipVisible, modalVisible } = useShouldShowModal(tooltipRef, false, forceShowModal);

    // Show modal if necessary.
    useEffect(() => {
        if (!hide && modalVisible && show) {
            dispatch(showModal({ type: MODAL_CONTENT, useHTML, onClose, content: content || children || "" }));
        }
    }, [hide, modalVisible, show]); // eslint-disable-line react-hooks/exhaustive-deps

    return {
        tooltipVisible,
        modalVisible,
        tooltipRef,
        pointOffset: align,
        show,
        offsetWithArrowLocation: { x: xPos, y: yPos },
    };
};
